import React from 'react';
import { ProjetCalcule } from '../types';
import ProjetProgressBar from './ProjetProgressBar';
import {
  MapPinIcon,
  UserIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';

interface ProjetCardProps {
  projet: ProjetCalcule;
  onOuvrir: (projetId: string) => void;
}

const fmt = (n: number) => n.toLocaleString('fr-FR');

const statutBadge: Record<string, { bg: string; text: string; label: string }> = {
  en_cours: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
  termine: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
  en_pause: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En pause' },
  a_risque: { bg: 'bg-red-100', text: 'text-red-800', label: 'À risque' },
};

const ProjetCard: React.FC<ProjetCardProps> = ({ projet, onOuvrir }) => {
  const badge = statutBadge[projet.statut] || statutBadge.en_cours;
  const ecartBudget = projet.budgetActuel - projet.budgetSigne;
  const hasDepassement = ecartBudget > 0;

  return (
    <div
      onClick={() => onOuvrir(projet.id)}
      className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-violet-300 hover:bg-violet-50/30 cursor-pointer transition-all group"
    >
      {/* Nom + infos */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <h3 className="text-base font-semibold text-gray-900 truncate">{projet.nom}</h3>
          <span className={`px-2 py-0.5 text-xs rounded-full ${badge.bg} ${badge.text} flex-shrink-0`}>
            {badge.label}
          </span>
          {hasDepassement && (
            <span className="flex items-center text-xs text-red-600 flex-shrink-0">
              <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-0.5" />
              +{fmt(ecartBudget)} €
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center truncate">
            <MapPinIcon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
            {projet.adresse}
          </span>
          <span className="flex items-center flex-shrink-0">
            <UserIcon className="w-3.5 h-3.5 mr-1" />
            {projet.client}
          </span>
          <span className="flex-shrink-0">{projet.lots.length} lots</span>
        </div>
        {/* Alertes inline */}
        {projet.alertes.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1.5">
            {projet.alertes.map((alerte, i) => (
              <span key={i} className="inline-flex items-center text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5">
                <ExclamationTriangleIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                {alerte}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Budget */}
      <div className="hidden md:flex items-center gap-6 flex-shrink-0">
        <div className="text-right">
          <div className="text-xs text-gray-500">Montant contractuel</div>
          <div className="text-sm font-semibold text-gray-900">{fmt(projet.budgetSigne)} €</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Avancement facturé</div>
          <div className="text-sm font-semibold text-gray-900">{fmt(projet.cumulTotal)} €</div>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1 text-xs text-gray-500">
            MAT
            {projet.mat.tendance === 'amelioration' && <ArrowTrendingUpIcon className="w-3 h-3 text-emerald-500" />}
            {projet.mat.tendance === 'degradation' && <ArrowTrendingDownIcon className="w-3 h-3 text-red-500" />}
            {projet.mat.tendance === 'stable' && <MinusIcon className="w-3 h-3 text-gray-400" />}
          </div>
          <div className={`text-sm font-semibold ${projet.mat.margeATerminaison >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {projet.mat.margeATerminaison >= 0 ? '+' : ''}{fmt(Math.round(projet.mat.margeATerminaison))} €
          </div>
          <div className={`text-[10px] ${projet.mat.tauxMAT >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {projet.mat.tauxMAT.toFixed(1)}%
          </div>
        </div>
        {projet.incidentsOuverts.length > 0 && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-600">
            <ExclamationTriangleIcon className="w-3 h-3 mr-0.5" />
            {projet.incidentsOuverts.length} inc.
          </span>
        )}
      </div>

      {/* Progress */}
      <div className="hidden md:block w-36 flex-shrink-0">
        <ProjetProgressBar
          pourcentage={projet.pourcentageFinancier}
          color="bg-violet-600"
          height="h-2"
        />
        <div className="flex justify-between mt-0.5">
          <span className="text-[10px] text-gray-400">Financier</span>
          <span className="text-[10px] font-medium text-gray-600">{projet.pourcentageFinancier}%</span>
        </div>
        <ProjetProgressBar
          pourcentage={projet.pourcentageTemporel}
          color="bg-blue-400"
          height="h-1.5"
        />
        <div className="flex justify-between mt-0.5">
          <span className="text-[10px] text-gray-400">Temporel</span>
          <span className="text-[10px] font-medium text-gray-600">{projet.pourcentageTemporel}%</span>
        </div>
      </div>

      {/* Chevron */}
      <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-violet-500 transition-colors flex-shrink-0" />
    </div>
  );
};

export default ProjetCard;
